import React from "react";
import { api, fmtINR } from "../../lib/apiClient";
import { UNITS } from "../../lib/constants";
import { Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { SearchableSelect } from "../SearchableSelect";

// Editable line-items grid: product typeahead, HSN lookup, qty/rate/discount/GST and per-line amount.
export function LineItemsTable({ form, setForm, cfg, products, setItem, pickProduct, addRow, removeRow, setQuickProductIdx }) {
  return (
  <div className="card-base p-0 overflow-hidden">
    <div className="flex items-center justify-between px-5 py-3 border-b border-border">
      <div className="font-display font-semibold">Line Items</div>
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none">
          <input
            type="checkbox"
            checked={!!form.rate_includes_gst}
            onChange={e => setForm({ ...form, rate_includes_gst: e.target.checked })}
            data-testid="rate-includes-gst-toggle"
          />
          <span className="font-medium">Rates include GST</span>
        </label>
        <button onClick={addRow} className="btn-secondary inline-flex items-center gap-1" data-testid="add-line-item"><Plus size={14} /> Add Row</button>
      </div>
    </div>
    <div className="overflow-x-auto">
      <table className="table-base min-w-[1100px]">
        <thead><tr>
          <th className="w-10">#</th>
          <th className="min-w-[300px]">Item / Product</th>
          <th>HSN</th>
          <th>Unit</th>
          <th className="text-right">Qty</th>
          <th className="text-right">Rate{form.rate_includes_gst ? " (incl GST)" : ""}</th>
          <th className="text-right">Disc %</th>
          {cfg.showTax && <th className="text-right">GST %</th>}
          <th className="text-right">Amount</th>
          <th></th>
        </tr></thead>
        <tbody>
          {form.items.map((it, i) => {
            const taxable = (Number(it.quantity) * Number(it.rate)) * (1 - Number(it.discount_pct || 0) / 100);
            return (
              <tr key={it._uid || `row-${i}`} data-testid={`line-row-${i}`}>
                <td className="text-ink-muted">{i + 1}</td>
                <td>
                  <div className="flex items-center gap-1 mb-1">
                    <SearchableSelect
                      className="flex-1"
                      testId={`line-product-${i}`}
                      options={products.map(p => ({ value: p.id, label: p.name, sub: [p.hsn ? `HSN ${p.hsn}` : "", p.rate ? `₹${p.rate}` : "", p.unit].filter(Boolean).join(" · "), keywords: [p.hsn, p.description].filter(Boolean) }))}
                      value={it.product_id}
                      onChange={v => pickProduct(i, v)}
                      placeholder="Type item name or HSN to search…"
                    />
                    <button
                      type="button"
                      onClick={() => setQuickProductIdx(i)}
                      className="btn-secondary !px-2 !py-1 text-xs whitespace-nowrap inline-flex items-center gap-1"
                      title="Quick-add product"
                      data-testid={`line-quick-add-product-${i}`}
                    >
                      <Plus size={12} weight="bold" /> New
                    </button>
                  </div>
                  <input className="field" placeholder="Item name" value={it.name} onChange={e => setItem(i, { name: e.target.value })} data-testid={`line-name-${i}`} />
                  <input className="field mt-1 text-xs" placeholder="Description (optional)" value={it.description} onChange={e => setItem(i, { description: e.target.value })} />
                </td>
                <td>
                  <input
                    className="field font-mono"
                    style={{ width: 110 }}
                    value={it.hsn}
                    onChange={e => setItem(i, { hsn: e.target.value })}
                    onBlur={async () => {
                      const code = (it.hsn || "").trim();
                      if (!code || code.length < 2) return;
                      try {
                        const { data } = await api.get(`/lookup/hsn/${code}`);
                        if (typeof data.gst_rate === "number") {
                          setItem(i, { gst_rate: data.gst_rate });
                          toast.success(`GST ${data.gst_rate}% — ${data.description}`, { duration: 2200 });
                        }
                      } catch (err) { console.warn('HSN lookup failed; keeping current GST rate', err); }
                    }}
                    data-testid={`line-hsn-${i}`}
                    placeholder="HSN/SAC"
                  />
                </td>
                <td>
                  <select className="field" style={{ width: 80 }} value={it.unit} onChange={e => setItem(i, { unit: e.target.value })}>
                    {UNITS.map(u => <option key={u}>{u}</option>)}
                  </select>
                </td>
                <td className="text-right"><input type="number" step="1" min="0" className="field text-right" style={{ width: 90 }} value={it.quantity} onChange={e => setItem(i, { quantity: e.target.value })} data-testid={`line-qty-${i}`} /></td>
                <td className="text-right"><input type="number" step="0.01" className="field text-right" style={{ width: 100 }} value={it.rate} onChange={e => setItem(i, { rate: e.target.value })} data-testid={`line-rate-${i}`} /></td>
                <td className="text-right"><input type="number" step="0.01" className="field text-right" style={{ width: 80 }} value={it.discount_pct} onChange={e => setItem(i, { discount_pct: e.target.value })} /></td>
                {cfg.showTax && (
                  <td className="text-right">
                    <select className="field text-right" style={{ width: 80 }} value={it.gst_rate} onChange={e => setItem(i, { gst_rate: Number(e.target.value) })} data-testid={`line-gst-${i}`}>
                      {[0, 0.25, 3, 5, 12, 18, 28].map(r => <option key={r} value={r}>{r}%</option>)}
                    </select>
                  </td>
                )}
                <td className="text-right font-semibold tabular">{fmtINR(taxable)}</td>
                <td className="text-right"><button onClick={() => removeRow(i)} className="btn-ghost text-destructive" data-testid={`remove-line-${i}`}><Trash size={14} /></button></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
  );
}
